import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, ArrowRight, Heart, Target, Users, Globe } from "lucide-react";

export default function AboutPage() {
  const values = [
    {
      icon: Target,
      title: "Transparency First",
      description: "We never make claims we can't back up. Every sustainability statement comes with proof, documentation, or methodology."
    },
    {
      icon: Users,
      title: "Farmer Partnership",
      description: "We work with farmers as partners, not suppliers. Fair compensation and long-term relationships benefit everyone."
    },
    {
      icon: Globe,
      title: "Local Production",
      description: "Processing wool within the Netherlands and Germany preserves skills, reduces emissions, and keeps value in the region."
    },
    {
      icon: Heart,
      title: "Quality Over Quantity",
      description: "We make fewer, better products. Each piece is designed to last for years, not seasons."
    }
  ];

  const team = [
    {
      name: "Léon Roskam",
      role: "Team Member O",
      bio: "Came up with WoolStory"
    },
    {
      name: "Alice Marutyan",
      role: "Team Member 1",
      bio: "Student"
    },
    {
      name: "Valeria Quevedo Justiniano",
      role: "Team Member 2",
      bio: "Student"
    },
    {
      name: "Ignacio Lopez",
      role: "Team Member 3",
      bio: "Student"
    },
    {
      name: "Moritz Vonk",
      role: "Team Member 4",
      bio: "Student"
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">About</span>
          </nav>

          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground">
                About WoolStory
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                WoolStory exists because of a paradox: the Netherlands has over one million sheep, 
                but most of their wool goes to waste. We're changing that—one traceable product at a time.
              </p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Founded in Enschede in 2025, we partner with Dutch farms to collect wool that would 
                otherwise be discarded, then transform it into premium products through local processing 
                partners in the Netherlands and Germany.
              </p>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1534639077088-d702bcf685e7?w=800&q=80"
                  alt="Natural wool textiles"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16">
            {/* Vision */}
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Our Vision
              </span>
              <h2 className="font-display text-3xl font-semibold text-foreground mt-2">
                A world where no good fiber goes to waste
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                We imagine a future where every sheep's wool has value, where local production is 
                the norm rather than the exception, and where consumers can truly understand where 
                their products come from.
              </p>
            </div>

            {/* Mission */}
            <div className="mt-8 lg:mt-0">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Our Mission
              </span>
              <h2 className="font-display text-3xl font-semibold text-foreground mt-2">
                Transform discarded wool into traceable products
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                We collect wool that Dutch farmers can't sell, process it entirely within NL/DE, 
                craft it into beautiful products, and document every step. We prove that sustainable, 
                local, transparent fashion is not just possible—it's better.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground text-center mb-12">
            The Problem We're Solving
          </h2>

          <div className="space-y-8">
            <Card className="bg-secondary/30">
              <CardContent className="p-6">
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  Dutch wool has lost its market
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Over the past decades, global wool markets consolidated around a few dominant producers 
                  (Australia, New Zealand, UK). Dutch wool, while excellent quality, became economically 
                  unviable for traditional textile supply chains. The infrastructure to process it locally 
                  largely disappeared.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-secondary/30">
              <CardContent className="p-6">
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  Shearing is mandatory, but wool has no destination
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Sheep must be sheared at least once yearly for animal welfare—their wool grows 
                  continuously. But with no buyers, farmers face a dilemma: pay for disposal, burn 
                  it, bury it, or sell it in bulk for pennies to be exported overseas.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-secondary/30">
              <CardContent className="p-6">
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  Our solution: rebuild local value chains
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  WoolStory creates demand for wool that farmers couldn't sell. We pay fair prices, 
                  work with surviving local mills and artisans, and create products where the "story" 
                  and traceability are part of the value. We're proving there's a market for honest, 
                  local, waste-reducing fashion.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Our Values
            </h2>
            <p className="mt-4 text-muted-foreground">
              Principles that guide every decision at WoolStory.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-foreground">
                        {value.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              The Team
            </h2>
            <p className="mt-4 text-muted-foreground">
              A small team passionate about sustainable textiles and honest production.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="group bg-secondary/30 border-border/50">
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
                    <span className="font-display text-2xl font-semibold text-primary">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground">
                    {member.name}
                  </h3>
                  <p className="text-sm text-primary font-medium">{member.role}</p>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {member.name === "Moritz Vonk" ? (
                      <>
                        <span className="group-hover:hidden">{member.bio}</span>
                        <span className="hidden group-hover:inline">Built this Website</span>
                      </>
                    ) : (
                      member.bio
                    )}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Looking Forward */}
      <section className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">
            Looking Forward
          </h2>
          <p className="mt-6 text-lg opacity-90 leading-relaxed">
            As we grow, we're committed to scaling responsibly. That means:
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            <div className="bg-primary-foreground/10 rounded-xl p-6">
              <h3 className="font-medium mb-2">More Farms</h3>
              <p className="text-sm opacity-80">
                Partnering with additional Dutch farms to collect more waste wool.
              </p>
            </div>
            <div className="bg-primary-foreground/10 rounded-xl p-6">
              <h3 className="font-medium mb-2">Same Standards</h3>
              <p className="text-sm opacity-80">
                Never compromising on local production or full traceability.
              </p>
            </div>
            <div className="bg-primary-foreground/10 rounded-xl p-6">
              <h3 className="font-medium mb-2">Deeper Impact</h3>
              <p className="text-sm opacity-80">
                Expanding our proof and measurement systems as we scale.
              </p>
            </div>
          </div>
          <div className="mt-10">
            <Button asChild size="lg" variant="secondary" className="gap-2">
              <Link to="/products">
                Explore Our Products
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
            Want to learn more?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Whether you're a farmer interested in partnering, a retailer exploring wholesale, 
            or just curious about our process—we'd love to hear from you.
          </p>
          <Button variant="outline" className="mt-8 gap-2">
            Get in Touch
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
